import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PrimaryButton, Modal, useFetch } from "../../components/crm/ui";
import {
  Search, X, Filter, ChevronDown, ChevronUp, Mail, Bell,
  ExternalLink, Cpu, Hash, Pencil, Plus, Trash2,
} from "lucide-react";

function Chip({ tone, children }) {
  const t = tone || { bg: "bg-slate-100", fg: "text-slate-700", dot: "bg-slate-500" };
  return (
    <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider " + t.bg + " " + t.fg}>
      <span className={"w-1.5 h-1.5 rounded-full " + t.dot} />
      {children}
    </span>
  );
}

const WARRANTY_TONE = {
  expiring: { bg: "bg-amber-100", fg: "text-amber-800", dot: "bg-amber-500" },
  active:   { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500" },
  expired:  { bg: "bg-rose-100", fg: "text-rose-800", dot: "bg-rose-500" },
};

function warrantyTone(r) {
  if (!r.is_active) return "expired";
  if (r.days_left <= 30) return "expiring";
  return "active";
}

export default function InstalledBase() {
  const { data: rows, reload } = useFetch("/installed-base");
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [detailId, setDetailId] = useState(null);

  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [fIb, setFIb] = useState("");
  const [fOpp, setFOpp] = useState("");
  const [fTicket, setFTicket] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fDate, setFDate] = useState("");

  useEffect(() => { api.get("/admin/expiry-reminders/history").then((r) => setHistory(r.data)).catch(() => {}); }, []);

  async function runReminders(dryRun) {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/run-expiry-reminders?within_days=30&dry_run=${dryRun}`);
      if (data.sent_count > 0) {
        toast.success(`${dryRun ? "[Dry run] Would send" : "Sent"} ${data.sent_count} reminder${data.sent_count > 1 ? "s" : ""}`);
      } else if (data.skipped_count > 0) {
        toast.info(`Skipped ${data.skipped_count} (already sent for current expiry)`);
      } else {
        toast.info("Nothing to send — no equipment expiring within 30 days.");
      }
      api.get("/admin/expiry-reminders/history").then((r) => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setRunning(false); }
  }

  const activeFilters = useMemo(() => [fIb, fOpp, fTicket, fCustomer, fDate].filter(Boolean).length, [fIb, fOpp, fTicket, fCustomer, fDate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (fIb && !(r.ib_number || "").toLowerCase().includes(fIb.trim().toLowerCase())) return false;
      if (fOpp && !(r.opportunity_code || "").toLowerCase().includes(fOpp.trim().toLowerCase())) return false;
      if (fTicket && !(r.ticket_number || "").toLowerCase().includes(fTicket.trim().toLowerCase())) return false;
      if (fCustomer && !(r.customer_name || "").toLowerCase().includes(fCustomer.trim().toLowerCase())) return false;
      if (fDate && (r.commissioning_date || "").slice(0, 10) !== fDate) return false;
      if (q) {
        const hay = [r.ib_number, r.opportunity_code, r.ticket_number, r.quotation_number, r.customer_name].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, fIb, fOpp, fTicket, fCustomer, fDate]);

  function clearFilters() {
    setFIb(""); setFOpp(""); setFTicket(""); setFCustomer(""); setFDate(""); setSearch("");
  }

  const stats = useMemo(() => {
    const totalEquipment = rows.reduce((n, r) => n + (r.equipment_count || 0), 0);
    const active = rows.filter((r) => r.is_active).length;
    const expiring = rows.filter((r) => r.is_active && r.days_left <= 30).length;
    const expired = rows.length - active;
    return { total: rows.length, totalEquipment, active, expiring, expired };
  }, [rows]);

  return (
    <div data-testid="installed-base-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#4A5568]">After-Sales</div>
          <h1 className="font-display text-[22px] font-bold tracking-tight leading-none mt-1">Installed Base</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => runReminders(true)} disabled={running}
            className="border border-[#0A1128] px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-[#0A1128] hover:text-white transition-colors disabled:opacity-60"
            data-testid="reminder-dry-run">
            <Bell className="w-3 h-3 inline mr-1" /> Dry run
          </button>
          <PrimaryButton onClick={() => runReminders(false)} disabled={running} testid="reminder-send-btn">
            <Mail className="w-4 h-4" />
            {running ? "Sending…" : "Send 30-day reminders"}
          </PrimaryButton>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-3">
        <KpiChip label="IB Records" value={stats.total} />
        <KpiChip label="Equipment" value={stats.totalEquipment} tone="bg-slate-50 text-slate-800" />
        <KpiChip label="In Warranty" value={stats.active} tone="bg-emerald-50 text-emerald-800" />
        <KpiChip label="Expiring ≤30d" value={stats.expiring} tone="bg-amber-50 text-amber-800" />
        <KpiChip label="Out of Warranty" value={stats.expired} tone="bg-rose-50 text-rose-800" />
      </div>

      {/* Search + filter row */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 flex items-center gap-2 border border-[#DDE5D8] bg-white px-2 py-1.5">
          <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search IB #, opp code, ticket #, quote #, customer…"
            data-testid="ib-search"
            className="flex-1 text-[12.5px] font-mono outline-none bg-transparent"
          />
          {search && <button onClick={() => setSearch("")}><X className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#0F1A12]" /></button>}
        </div>
        <button
          onClick={() => setFilterOpen((v) => !v)}
          data-testid="ib-filter-toggle"
          className={"px-2.5 py-1.5 border font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 " + (filterOpen ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#0F1A12] border-[#DDE5D8] hover:border-[#1F4E2C]")}
        >
          <Filter className="w-3.5 h-3.5" /> Filters{activeFilters > 0 && ` · ${activeFilters}`}
          {filterOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters} data-testid="ib-filter-clear" className="text-[10.5px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] px-2">Reset</button>
        )}
        <div className="font-mono text-[10.5px] text-[#475569]">
          <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {rows.length}
        </div>
      </div>

      {filterOpen && (
        <div className="mb-3 border border-[#DDE5D8] bg-[#FFFDF8] p-3 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ib-filter-panel">
          <FilterField label="IB Number" value={fIb} onChange={setFIb} placeholder="IB-1001…" testid="ff-ib" />
          <FilterField label="Opportunity Code" value={fOpp} onChange={setFOpp} placeholder="OPP-2026-…" testid="ff-opp" />
          <FilterField label="Ticket Reference" value={fTicket} onChange={setFTicket} placeholder="TKT-1001…" testid="ff-ticket" />
          <FilterField label="Customer Name" value={fCustomer} onChange={setFCustomer} placeholder="customer name…" testid="ff-customer" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Date of Installation</div>
            <input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="ff-date" className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono" />
          </div>
        </div>
      )}

      <div className="border border-[#DDE5D8] bg-white overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="ib-table">
          <thead>
            <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569] bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <th className="px-2.5 py-2 whitespace-nowrap">IB Number</th>
              <th className="px-2.5 py-2">Customer</th>
              <th className="px-2.5 py-2">Opportunity</th>
              <th className="px-2.5 py-2">Ticket Ref</th>
              <th className="px-2.5 py-2">Quotation</th>
              <th className="px-2.5 py-2 text-right">Equipment</th>
              <th className="px-2.5 py-2 whitespace-nowrap">Installed On</th>
              <th className="px-2.5 py-2">Comprehensive Warranty</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`ib-row-${r.ib_number}`}
                className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer"
                onClick={() => setDetailId(r.id)}>
                <td className="px-2.5 py-2 font-mono text-[11px] font-semibold text-[#1F4E2C] whitespace-nowrap">{r.ib_number}</td>
                <td className="px-2.5 py-2">{r.customer_name || "—"}</td>
                <td className="px-2.5 py-2" onClick={(e) => e.stopPropagation()}>
                  {r.opportunity_code ? (
                    <Link to={`/crm/opportunities/${r.opportunity_id}`} className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-[#1F4E2C] hover:underline">
                      {r.opportunity_code}<ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  ) : <span className="text-[#94A3B8] font-mono text-[11px]">—</span>}
                </td>
                <td className="px-2.5 py-2 font-mono text-[11px]">{r.ticket_number || <span className="text-[#94A3B8]">—</span>}</td>
                <td className="px-2.5 py-2 font-mono text-[11px]">{r.quotation_number || <span className="text-[#94A3B8]">—</span>}</td>
                <td className="px-2.5 py-2 text-right font-mono text-[11px]"><Cpu className="w-3 h-3 inline mr-1 text-[#94A3B8]" />{r.equipment_count}</td>
                <td className="px-2.5 py-2 font-mono text-[10.5px] text-[#475569] whitespace-nowrap">{r.commissioning_date ? new Date(r.commissioning_date).toLocaleDateString() : "—"}</td>
                <td className="px-2.5 py-2">
                  <Chip tone={WARRANTY_TONE[warrantyTone(r)]}>
                    {r.is_active ? (r.days_left <= 30 ? `Expires ${r.days_left}d` : `${r.days_left}d left`) : `Expired ${Math.abs(r.days_left)}d ago`}
                  </Chip>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[13px] text-[#475569]">
                {rows.length === 0 ? "No Installed Base records yet." : "No records match the filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {history.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-[#4A5568]" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">Reminder history (last {history.length})</div>
          </div>
          <div className="border border-[#E2E4DC] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5568] border-b border-[#E2E4DC]">
                  <th className="px-4 py-3">Sent at</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 text-right">Days-left at send</th>
                  <th className="px-4 py-3">Resend ID</th>
                  <th className="px-4 py-3">Delivered</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 20).map((h) => (
                  <tr key={h.id} className="border-b border-[#E2E4DC]">
                    <td className="px-4 py-3 font-mono text-xs">{new Date(h.sent_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs">{h.email}</td>
                    <td className="px-4 py-3 text-right font-mono">{h.days_left_at_send}</td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[#4A5568]">{h.resend_id || "—"}</td>
                    <td className="px-4 py-3">
                      {h.delivered ? <Chip tone={WARRANTY_TONE.active}>Sent</Chip> : <Chip tone={WARRANTY_TONE.expired}>Failed</Chip>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InstalledBaseDetailModal ibId={detailId} onClose={() => setDetailId(null)} onUpdated={reload} />
    </div>
  );
}

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2 py-1 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[15px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

function FilterField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono" />
    </div>
  );
}

function groupEquipmentByMaterial(equipment) {
  const byMaterial = [];
  const idx = {};
  for (const eq of equipment) {
    if (idx[eq.material_id] === undefined) {
      idx[eq.material_id] = byMaterial.length;
      byMaterial.push({
        material_id: eq.material_id, material_code: eq.material_code, material_name: eq.material_name,
        description: eq.description, warranty_days: eq.warranty_days, units: [],
      });
    }
    byMaterial[idx[eq.material_id]].units.push({ id: eq.id, serial_code: eq.serial_code, is_extra: eq.is_extra });
  }
  return byMaterial;
}

function InstalledBaseDetailModal({ ibId, onClose, onUpdated }) {
  const [ib, setIb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [editNotes, setEditNotes] = useState("");
  const [editWarrantyDays, setEditWarrantyDays] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: materials } = useFetch("/materials");

  useEffect(() => {
    if (!ibId) { setIb(null); setEditMode(false); return; }
    setLoading(true);
    api.get(`/installed-base/${ibId}`).then((r) => setIb(r.data)).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }).finally(() => setLoading(false));
  }, [ibId]);

  function startEdit() {
    setEditLines(groupEquipmentByMaterial(ib.equipment));
    setEditNotes(ib.notes || "");
    setEditWarrantyDays(ib.comprehensive_warranty_days ?? "");
    setEditMode(true);
  }

  function addEditLine() {
    setEditLines((prev) => [...prev, {
      material_id: "", material_code: "", material_name: "", description: "", warranty_days: null,
      units: [{ id: null, serial_code: "", is_extra: true }],
    }]);
  }

  function setEditLineMaterial(i, materialId) {
    const mat = materials.find((m) => m.id === materialId);
    setEditLines((prev) => prev.map((l, idx) => idx !== i ? l : {
      ...l, material_id: materialId, material_code: mat?.code || "", material_name: mat?.name || "", description: mat?.description || "",
      warranty_days: l.warranty_days ?? mat?.warranty_days ?? null,
    }));
  }

  function setLineWarrantyDays(i, value) {
    setEditLines((prev) => prev.map((l, idx) => idx !== i ? l : { ...l, warranty_days: value === "" ? null : Math.max(0, Number(value) || 0) }));
  }

  function setEditQuantity(i, qty) {
    const q = Math.max(1, Number(qty) || 1);
    setEditLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const units = [...l.units];
      while (units.length < q) units.push({ id: null, serial_code: "", is_extra: true });
      while (units.length > q) units.pop();
      return { ...l, units };
    }));
  }

  function setEditSerial(i, j, value) {
    setEditLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const units = [...l.units];
      units[j] = { ...units[j], serial_code: value };
      return { ...l, units };
    }));
  }

  function removeEditLine(i) {
    setEditLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function saveEdit() {
    if (editLines.some((l) => !l.material_id)) {
      toast.error("Every line needs a material selected");
      return;
    }
    if (editLines.some((l) => l.units.some((u) => !u.serial_code.trim()))) {
      toast.error("Enter the serial number for every unit");
      return;
    }
    const equipment = editLines.flatMap((l) => l.units.map((u) => ({
      id: u.id || undefined, material_id: l.material_id, serial_code: u.serial_code.trim(), is_extra: u.is_extra,
      warranty_days: l.warranty_days ?? null,
    })));
    setSaving(true);
    try {
      const { data } = await api.put(`/installed-base/${ib.id}`, {
        equipment, notes: editNotes,
        comprehensive_warranty_days: editWarrantyDays === "" ? null : Number(editWarrantyDays),
      });
      setIb(data);
      setEditMode(false);
      toast.success(`Installed Base ${data.ib_number} updated`);
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={!!ibId} onClose={onClose} title={ib ? `Installed Base · ${ib.ib_number}` : "Installed Base"} wide>
      {loading && <div className="text-xs font-mono text-[#94A3B8] py-6 text-center">Loading…</div>}
      {ib && !loading && (
        <div className="space-y-4" data-testid="ib-detail">
          {/* Header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-[#DDE5D8] bg-[#F4F6F1] p-3">
            <HeaderField label="IB Number" value={ib.ib_number} accent />
            <HeaderField label="Customer" value={ib.customer?.customer_name} sub={ib.customer?.phone} />
            <HeaderField label="Date of Installation" value={ib.commissioning_date ? new Date(ib.commissioning_date).toLocaleDateString() : "—"} />
            <HeaderField
              label="Warranty End Date"
              value={ib.warranty_valid_date ? new Date(ib.warranty_valid_date).toLocaleDateString() : "—"}
              accentTone={ib.comprehensive_warranty.is_active ? "success" : "danger"}
            />
            <HeaderField
              label="Warranty Status"
              value={ib.comprehensive_warranty.is_active
                ? `In Warranty · ${ib.comprehensive_warranty.days_left}d left (of ${ib.comprehensive_warranty_days}d)`
                : `Out of Warranty · expired ${Math.abs(ib.comprehensive_warranty.days_left)}d ago`}
              accentTone={ib.comprehensive_warranty.is_active ? "success" : "danger"}
            />
            <HeaderField label="Opportunity" value={ib.opportunity_code || "—"} />
            <HeaderField label="Ticket Reference" value={ib.ticket_number || "—"} />
            <HeaderField label="Quotation" value={ib.quotation_number || "—"} />
            <HeaderField label="Customer Address" value={ib.customer?.address || "—"} />
          </div>

          {!editMode && ib.notes && (
            <div className="text-sm text-[#4A5568] italic border-l-2 border-[#DDE5D8] pl-3">{ib.notes}</div>
          )}

          {/* Line items — Equipment */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1.5">
                <Hash className="w-3 h-3" /> Equipment ({editMode ? editLines.reduce((n, l) => n + l.units.length, 0) : ib.equipment.length})
              </div>
              {!editMode ? (
                <button type="button" onClick={startEdit} data-testid="ib-edit-btn"
                  className="text-[10.5px] font-mono uppercase tracking-widest text-[#1F4E2C] inline-flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              ) : (
                <button type="button" onClick={addEditLine} data-testid="ib-edit-add-line"
                  className="text-[10.5px] font-mono uppercase tracking-widest text-[#1F4E2C] inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add material
                </button>
              )}
            </div>

            {!editMode && (
              <div className="border border-[#DDE5D8] bg-white overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="ib-equipment-table">
                  <thead>
                    <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569] bg-[#F4F6F1] border-b border-[#DDE5D8]">
                      <th className="px-2.5 py-2">Material Number</th>
                      <th className="px-2.5 py-2">Description</th>
                      <th className="px-2.5 py-2 text-right">Quantity</th>
                      <th className="px-2.5 py-2">Serial Number</th>
                      <th className="px-2.5 py-2">Warranty End Date</th>
                      <th className="px-2.5 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ib.equipment.map((eq) => (
                      <tr key={eq.id} className="border-b border-[#EEF2EA]" data-testid={`ib-equipment-row-${eq.serial_code}`}>
                        <td className="px-2.5 py-2 font-mono text-[11px] font-semibold text-[#1F4E2C]">{eq.material_code || "—"}</td>
                        <td className="px-2.5 py-2">
                          <div className="font-medium">{eq.material_name || "—"}</div>
                          {eq.description && <div className="text-[10.5px] text-[#475569]">{eq.description}</div>}
                          {eq.is_extra && <span className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 px-1 py-0.5 inline-block mt-0.5">Extra</span>}
                        </td>
                        <td className="px-2.5 py-2 text-right font-mono">{eq.quantity}</td>
                        <td className="px-2.5 py-2 font-mono text-[11px]">{eq.serial_code}</td>
                        <td className="px-2.5 py-2 font-mono text-[11px]" data-testid={`ib-equipment-warranty-end-${eq.serial_code}`}>
                          {eq.expiry_date ? new Date(eq.expiry_date).toLocaleDateString() : "—"}
                          <div className="text-[9.5px] font-mono text-[#94A3B8]">{eq.warranty_days}d cover</div>
                        </td>
                        <td className="px-2.5 py-2">
                          <Chip tone={WARRANTY_TONE[warrantyTone(eq)]}>
                            {eq.is_active ? `In Warranty · ${eq.days_left}d left` : `Out of Warranty`}
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {editMode && (
              <div className="space-y-3" data-testid="ib-edit-lines">
                {editLines.map((line, i) => (
                  <div key={i} className="border border-[#E2E4DC] p-3" data-testid={`ib-edit-line-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <select value={line.material_id} onChange={(e) => setEditLineMaterial(i, e.target.value)}
                          data-testid={`ib-edit-mat-${i}`}
                          className="w-full border border-[#E2E4DC] px-2 py-1.5 text-sm font-mono">
                          <option value="">— Select material —</option>
                          {materials.map((m) => <option key={m.id} value={m.id}>{`${m.code} · ${m.name}`}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">Warranty (days)</div>
                          <input type="number" min={0} value={line.warranty_days ?? ""}
                            onChange={(e) => setLineWarrantyDays(i, e.target.value)}
                            placeholder="Default"
                            data-testid={`ib-edit-warranty-${i}`}
                            className="w-20 border border-[#E2E4DC] px-2 py-1 text-sm font-mono text-right" />
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">Qty</div>
                          <input type="number" min={1} value={line.units.length}
                            onChange={(e) => setEditQuantity(i, e.target.value)}
                            data-testid={`ib-edit-qty-${i}`}
                            className="w-16 border border-[#E2E4DC] px-2 py-1 text-sm font-mono text-right" />
                        </div>
                        <button type="button" onClick={() => removeEditLine(i)} className="text-red-600 mt-4" data-testid={`ib-edit-remove-line-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {line.units.map((u, j) => (
                        <input key={j} value={u.serial_code}
                          onChange={(e) => setEditSerial(i, j, e.target.value)}
                          placeholder={`Serial · Unit ${j + 1} *`}
                          data-testid={`ib-edit-serial-${i}-${j}`}
                          className="border border-[#E2E4DC] px-2 py-1.5 text-sm font-mono" />
                      ))}
                    </div>
                  </div>
                ))}
                {editLines.length === 0 && (
                  <div className="text-xs font-mono text-[#94A3B8] py-3 border border-dashed border-[#DDE5D8] px-3">
                    No equipment lines — use "Add material" to register at least one.
                  </div>
                )}
              </div>
            )}
          </div>

          {editMode && (
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">
                  Comprehensive Warranty (days) <span className="text-[#94A3B8]">— header-level, drives Warranty End Date above</span>
                </div>
                <input type="number" min={0} value={editWarrantyDays}
                  onChange={(e) => setEditWarrantyDays(e.target.value)}
                  placeholder="Auto = longest line-item warranty"
                  data-testid="ib-edit-comprehensive-warranty-days"
                  className="w-full border border-[#E2E4DC] px-2 py-1.5 text-sm font-mono" />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Notes</div>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                  data-testid="ib-edit-notes"
                  className="w-full border border-[#E2E4DC] px-2 py-1.5 text-sm" rows={2} />
              </div>
            </div>
          )}

          {editMode && (
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={() => setEditMode(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
              <PrimaryButton onClick={saveEdit} disabled={saving} testid="ib-edit-save">
                {saving ? "Saving…" : "Save Changes"}
              </PrimaryButton>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function HeaderField({ label, value, sub, accent, accentTone }) {
  const toneClass = accentTone === "success" ? "text-emerald-700" : accentTone === "danger" ? "text-red-600" : "text-[#0F1A12]";
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#4A5568]">{label}</div>
      <div className={"text-[13px] font-semibold mt-0.5 " + (accent ? "text-[#1F4E2C] font-mono" : toneClass)}>{value || "—"}</div>
      {sub && <div className="text-[10.5px] text-[#475569] font-mono">{sub}</div>}
    </div>
  );
}
